import { useEffect, useState } from "react";
import { Card, Col, DatePicker, Form, InputNumber, Radio, Row, TimePicker, Typography, Select } from "antd";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from 'moment';
import FormWrapper from "../FormWrapper";
import { getClasses } from "containers/Classes/actions";
import "./RepositionModal.scss";
import { createRepositions } from "reducers/group-cancellation/actions";
import {
  DATABASE_DATE_FORMAT,
  DATABASE_DATE_TIME_FORMAT,
  FRACTION_DURATION,
  REPLACEMENT_TYPES,
  REPLACEMENT_POSITION_OPTIONS,
  POSITION_VALUES,
  REPLACEMENT_TYPE_VALUES,
  TIME_PICKER_CONFIG,
  DATE_PICKER_CONFIG,
  INPUT_NUMBER_CONFIG,
  VALIDATION_MESSAGES,
  CSS_CLASSES,
  TEXT
} from "./constants";

function RepositionModal(props) {
  const {
    classReducer,
    reposition,
    getClasses,
    onClose,
    groupCancellationReducer,
    createRepositions,
  } = props;
  const [classStartTime, classEndTime ] = reposition.scheduleTime.split(' - ');
  const classStartDateTime = moment(`${reposition.scheduleDate} ${classStartTime}`, DATABASE_DATE_TIME_FORMAT);
  const classEndDateTime = moment(`${reposition.scheduleDate} ${classEndTime}`, DATABASE_DATE_TIME_FORMAT);

  // Calculate duration from scheduleTime format "16:00 - 17:00"
  const durationMinutes = classEndDateTime.diff(classStartDateTime, 'minutes');
  const maxParts = Math.floor(durationMinutes / FRACTION_DURATION);
  const replacementTypes = REPLACEMENT_TYPES.map(type => ({ ...type, disabled: type.value === REPLACEMENT_TYPE_VALUES.COMPLETE && reposition.availableParts < maxParts }));

  const [values, setValues] = useState({
    replacementType: replacementTypes.find(type => type.disabled === false).value,
    partsPerClass: 1,
  });

  const [form] = Form.useForm();

  const onChange = (formValue) => {
    setValues({
      ...values,
      ...formValue,
    });
  };

  const mapPayload = (formFieldsValue) => {
    let payload = {
      trackingID: reposition.trackingID,
      studentsForAgreement: [],
      type: formFieldsValue.replacementType,
      replacementDetail: [],
      partsPerClass: formFieldsValue.partsPerClass || 1,
    };
    formFieldsValue.dates.map((date, repositionFormIndex) => {
      if (date) {
        const position = formFieldsValue.newClassTime?.[repositionFormIndex] ? undefined : (formFieldsValue.replacementPosition || [])[repositionFormIndex];
        const cancelledClassDuration = moment.duration(classEndDateTime.diff(classStartDateTime)).asMinutes();
        const repositionDate = date.format(DATABASE_DATE_FORMAT);
        const addBefore = position === POSITION_VALUES.ADD_BEFORE;
        const addAfter = position === POSITION_VALUES.ADD_AFTER;
        const isCompleteReposition = formFieldsValue.replacementType === REPLACEMENT_TYPE_VALUES.COMPLETE;
        const newClassTime = formFieldsValue.newClassTime?.[repositionFormIndex];
        
        // Calculate how many parts to use for this class
        const startIndex = repositionFormIndex;
        const remainingParts = reposition.availableParts - startIndex;
        const partsForThisClass = Math.min(formFieldsValue.partsPerClass || 1, remainingParts);
        const replacementOriginalTime = formFieldsValue.replacementTime?.[repositionFormIndex];

        let start, end;
        let isNewClass = false;
        
        if (newClassTime && formFieldsValue.replacementType === REPLACEMENT_TYPE_VALUES.BY_PARTS) {
          // If it's a new class, use the selected time
          isNewClass = true;
          start = moment(`${repositionDate} ${newClassTime.format('HH:mm')}`, DATABASE_DATE_TIME_FORMAT);
          end = moment(start).add(FRACTION_DURATION * partsForThisClass, 'minutes');
        } else {
          // If it's an existing class, handle the before/after logic
          const repositionClassStartTime = replacementOriginalTime.split(' - ')[0];
          const repositionClassEndTime = replacementOriginalTime.split(' - ')[1];
          start = moment(`${repositionDate} ${repositionClassStartTime}`, DATABASE_DATE_TIME_FORMAT);
          end = moment(`${repositionDate} ${repositionClassEndTime}`, DATABASE_DATE_TIME_FORMAT);
          
          if (addBefore) {
            start.subtract(FRACTION_DURATION, 'minutes');
          }
          if (addAfter) {
            end.add(FRACTION_DURATION, 'minutes');
          }
        }
        if (isCompleteReposition) {
          start = moment(`${repositionDate} ${formFieldsValue.time.format('HH:mm')}`, DATABASE_DATE_TIME_FORMAT);
          end = moment(start).add(cancelledClassDuration, 'minutes');
        }
        
        payload.replacementDetail.push({
          ...reposition,
          id: formFieldsValue.repositionClassId?.[repositionFormIndex] || reposition.id,
          studentsForAgreement: true,
          start: start.format(DATABASE_DATE_TIME_FORMAT+':ss'),
          end: end.format(DATABASE_DATE_TIME_FORMAT+':ss'),
          endDatetime: end.valueOf(),
          startDatetime: start.valueOf(),
          addBefore,
          addAfter,
          isReplacement: true,
          isCancelled: false,
          isCurrentClass: false,
          isPartOfReplacement: !isCompleteReposition,
          isNewClass,
          partsUsed: partsForThisClass,
        });
      }
      return date;
    });

    return payload;
  };
  const onFinish = () => {
    const payloadMapped = mapPayload(form.getFieldsValue());
    createRepositions(reposition.id, payloadMapped);
  };
  
  const getClassesOfTheDay = (date) => {
    // Get all days that match the date (since a date can exist multiple times)
    const matchingDays = (classReducer.classesOrigin || []).filter((day) => day.date === date);
    // Flatten all classes from all matching days
    let classes = matchingDays.flatMap((day) => day.classes || []);
    classes = classes.filter((classItem) => classItem.students.some((student) => student.attendance === '') ||  classItem.students.length === 0);
    return classes;
  };

  const dateRender = (current, today) => {
    const cellDate = current.format(DATABASE_DATE_FORMAT);
    const classesOfTheDay = getClassesOfTheDay(cellDate);
    const isCancelledDate = cellDate === reposition.scheduleDate;
    const hasClasses = classesOfTheDay.length > 0;

    return (
      <div className={`ant-picker-cell-inner ${hasClasses && CSS_CLASSES.DAY_HAS_CLASSES} ${isCancelledDate && CSS_CLASSES.IS_CANCELLED_DATE}`}>
        {current.date()}
      </div>
    );
  };

  const panelRender = (panel) => {
    return (
      <Col>
        <Row>
          {panel}
        </Row>
      </Col>
    );
  };

  const fetchClasses = (date) => {
    const payload = {
      startdate: date.startOf('month').format('YYYY-MM-DD'),
      enddate: date.endOf('month').format('YYYY-MM-DD'),
      clients: [],
      teachers: [],
      languages: [],
      groups: [reposition.groupId],
      levels: [],
    };
    getClasses(payload);
  }

  const handleOnDatePickerMonthChange = (date) => {
    fetchClasses(date);
  };

  const handleOnRadioChange = (e, index, classItem) => {
    form.setFieldsValue({
      replacementTime: {
        ...form.getFieldValue('replacementTime'),
        [index]: classItem.scheduleTime
      },
      repositionClassId: {
        ...form.getFieldValue('repositionClassId'),
        [index]: classItem.id
      }
    });
  };

  const clearReplacementTime = () => {
    form.setFieldsValue({
      replacementPosition: [],
      repositionClassId: [],
      selectedClassId: []
    });
  };

  useEffect(() => {
    if (groupCancellationReducer.loading.CREATE_REPOSITIONS?.state === 'success') {
      window.location.reload();
    }
  }, [groupCancellationReducer.loading.CREATE_REPOSITIONS]);
  
  useEffect(() => {
    fetchClasses(moment());
  }, []);
  
  const originalClassDate = moment(reposition.scheduleDate, DATABASE_DATE_FORMAT).format('dddd DD [de] MMMM');

  const renderDatePicker = (index) => (
    <Form.Item
      label={TEXT.REPOSITION_DATE_LABEL(index)}
      name={['dates', index]}
    >
      <DatePicker 
        panelRender={panelRender} 
        popupClassName={DATE_PICKER_CONFIG.POPUP_CLASS_NAME} 
        onPanelChange={handleOnDatePickerMonthChange} 
        dateRender={dateRender} 
        onChange={clearReplacementTime}
      />
    </Form.Item>
  );

  const renderExistingClassOptions = (classItem, index) => (
    <Card type="inner">
      <Col>
        <Row>
          <Typography.Text><span>{TEXT.SCHEDULE_TIME_LABEL}</span> {classItem.scheduleTime}</Typography.Text>
          {values.replacementType === REPLACEMENT_TYPE_VALUES.BY_PARTS && (
            <>
              <Form.Item
                name={['replacementTime', index]}
                initialValue={classItem.scheduleTime}
                hidden
              >
                <input type="hidden" />
              </Form.Item>
              <Form.Item
                name={['repositionClassId', index]}
                initialValue={classItem.id}
                hidden
              >
                <input type="hidden" />
              </Form.Item>
            </>
          )}
        </Row>
        {renderReplacementOptions(index, classItem)}
      </Col>
    </Card>
  );

  const renderClassSelectionDropdown = (index, classesOfTheDay, selectedDate) => {
    const options = classesOfTheDay.map(classItem => ({
      label: `${classItem.scheduleTime} - ${classItem.classGroup || 'N/A'}`,
      value: classItem.id,
      classItem
    }));

    // Prepend new class option
    const newClassOption = {
      label: 'Nueva clase',
      value: 'new_class',
      classItem: null
    };
    const allOptions = [newClassOption, ...options];

    return (
      <Form.Item
        label={TEXT.SELECT_CLASS_LABEL || `Select Class for ${selectedDate?.format('dddd DD [de] MMMM')}`}
        name={['selectedClassId', index]}
        rules={[{ required: true, message: VALIDATION_MESSAGES.CLASS_SELECTION_REQUIRED }]}
      >
        <Select
          placeholder="Select a class"
          onChange={(value) => {
            if (value === 'new_class') {
              // Handle new class selection
              form.setFieldsValue({
                replacementTime: {
                  ...form.getFieldValue('replacementTime'),
                  [index]: null
                },
                repositionClassId: {
                  ...form.getFieldValue('repositionClassId'),
                  [index]: null
                }
              });
            } else {
              const selectedClass = classesOfTheDay.find(c => c.id === value);
              if (selectedClass) {
                form.setFieldsValue({
                  replacementTime: {
                    ...form.getFieldValue('replacementTime'),
                    [index]: selectedClass.scheduleTime
                  },
                  repositionClassId: {
                    ...form.getFieldValue('repositionClassId'),
                    [index]: selectedClass.id
                  }
                });
              }
            }
          }}
        >
          {allOptions.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  };

  const renderReplacementOptions = (index, classItem) => {
    if (values.replacementType !== REPLACEMENT_TYPE_VALUES.BY_PARTS) {
      return null;
    }

    return (
      <Row>
        <Form.Item
          label={TEXT.REPOSITION_DURATION_LABEL(FRACTION_DURATION * (values.partsPerClass || 1))}
          name={['replacementPosition', index]}
          rules={[{ required: true, message: VALIDATION_MESSAGES.REPLACEMENT_POSITION_REQUIRED }]}
        >
          <Radio.Group options={REPLACEMENT_POSITION_OPTIONS} onChange={(e) => handleOnRadioChange(e, index, classItem)} />
        </Form.Item>
      </Row>
    );
  };

  const renderNewClassTimePicker = (index, selectedDate) => (
    <>
      <Typography className="ant-form-item-label">
        {TEXT.NO_CLASSES_MESSAGE(selectedDate?.format('dddd DD [de] MMMM'))}
      </Typography>
      <Card type="inner">
        <Form.Item
          label={TEXT.REPOSITION_DURATION_LABEL(FRACTION_DURATION * (values.partsPerClass || 1))}
          name={['newClassTime', index]}
          rules={[{ required: true, message: VALIDATION_MESSAGES.NEW_CLASS_TIME_REQUIRED }]}
        >
          <TimePicker 
            popupClassName={TIME_PICKER_CONFIG.POPUP_CLASS_NAME} 
            minuteStep={TIME_PICKER_CONFIG.MINUTE_STEP} 
            use12Hours={TIME_PICKER_CONFIG.USE_12_HOURS}
            format={TIME_PICKER_CONFIG.FORMAT}
          />
        </Form.Item>
      </Card>
    </>
  );

  const renderRepositionTimePicker = () => {
    const isCompleteReposition = values.replacementType === REPLACEMENT_TYPE_VALUES.COMPLETE;
    const maxRepositions = isCompleteReposition ? 1 : Math.ceil(reposition.availableParts / (values.partsPerClass || 1));
    const selectedDates = form.getFieldValue('dates');
    const eventsOfSelectedDate = (selectedDates || []).map(date => getClassesOfTheDay(date?.format(DATABASE_DATE_FORMAT)) || []);
    
    return Array.from({ length: maxRepositions }).map((_, index) => 
      {
        const selectedClassId = form.getFieldValue(['selectedClassId', index]);
        return (
          <>
            {renderDatePicker(index)}
            {eventsOfSelectedDate[index]?.length > 0 && (
              <>
                <Typography className="ant-form-item-label">
                  {TEXT.SCHEDULED_CLASSES_LABEL(selectedDates[index]?.format('dddd DD [de] MMMM'))}
                </Typography>
                {isCompleteReposition ? (
                  eventsOfSelectedDate[index].map(classItem => renderExistingClassOptions(classItem, index))
                ) : (
                  <>
                    {renderClassSelectionDropdown(index, eventsOfSelectedDate[index], selectedDates[index])}
                    {selectedClassId && selectedClassId !== 'new_class' ? (
                      (() => {
                        const selectedClass = eventsOfSelectedDate[index].find(c => c.id === selectedClassId);
                        return selectedClass ? renderExistingClassOptions(selectedClass, index) : null;
                      })()
                    ) : (
                      <Form.Item
                        label={TEXT.SCHEDULE_LABEL}
                        name={['newClassTime', index]}
                        rules={[{ required: true, message: VALIDATION_MESSAGES.NEW_CLASS_TIME_REQUIRED }, {
                          validator: async (_, value) => {
                            const classesOfTheDay = getClassesOfTheDay(selectedDates[index]?.format(DATABASE_DATE_FORMAT));
                            const isTimeAvailable = classesOfTheDay.some((classItem) => classItem.scheduleTime.split(' - ')[0] !== value?.format('HH:mm'));
                            if (!isTimeAvailable) {
                              return Promise.reject(new Error(VALIDATION_MESSAGES.NEW_CLASS_TIME_ALREADY_EXISTS));
                            }
                            return Promise.resolve();
                          }
                        }]}
                      >
                        <TimePicker 
                          popupClassName={TIME_PICKER_CONFIG.POPUP_CLASS_NAME} 
                          minuteStep={TIME_PICKER_CONFIG.MINUTE_STEP} 
                          use12Hours={TIME_PICKER_CONFIG.USE_12_HOURS}
                          format={TIME_PICKER_CONFIG.FORMAT}
                        />
                      </Form.Item>
                    )}
                  </>
                )}
              </>
          )}
          {selectedDates?.[index] && values.replacementType === REPLACEMENT_TYPE_VALUES.BY_PARTS && !eventsOfSelectedDate[index]?.length && (
            renderNewClassTimePicker(index, selectedDates[index])
          )}
          {(values.replacementType === REPLACEMENT_TYPE_VALUES.COMPLETE || (!eventsOfSelectedDate[index]?.length && values.replacementType === REPLACEMENT_TYPE_VALUES.BY_PARTS)) && (
            <Form.Item
              name={['replacementTime', index]}
              initialValue={reposition.scheduleTime}
              hidden
            >
              <input type="hidden" />
            </Form.Item>
          )}
        </>
      );
    });
  };

  return (
    <div className={CSS_CLASSES.CLASS_MODAL}>
      <h1>{TEXT.MODAL_TITLE}</h1>
      <Card title={reposition.clientName} type="inner">
        <Col>
          <Row>
            <Typography.Text><span>{TEXT.GROUP_LABEL}</span> {reposition.classGroup}</Typography.Text>
          </Row>
          <Row>
            <Typography.Text><span>{TEXT.PREVIOUS_SCHEDULE_LABEL}</span> {originalClassDate} | {reposition.scheduleTime}</Typography.Text>
          </Row>
        </Col>
      </Card>
      <div>
        <FormWrapper
          name="class-reposition"
          form={form}
          initialValues={values}
          onValuesChange={onChange}
          onFinish={onFinish}
          loading={(classReducer.loading.GET_CLASSES?.state || groupCancellationReducer.loading.CREATE_REPOSITIONS?.state) === true}
          cancelButtonText={TEXT.CANCEL_BUTTON_TEXT}
          saveButtonText={TEXT.SAVE_BUTTON_TEXT}
          onRedirectCancelButton={() => onClose()}
        >
          <div className={CSS_CLASSES.CONTAINER_FORM}>
            <Form.Item
              label={TEXT.REPLACEMENT_TYPE_LABEL}
              name="replacementType"
              rules={[{ required: true }]}
            >
              <Radio.Group
                options={replacementTypes}
                onChange={clearReplacementTime}
              />
            </Form.Item>
            {values.replacementType === REPLACEMENT_TYPE_VALUES.BY_PARTS && (
              <>
                <Form.Item
                  label={TEXT.TO_SCHEDULE_LABEL}
                >
                  {TEXT.FRACTIONS_LABEL(reposition.availableParts)}
                </Form.Item>
                <Form.Item
                  label={TEXT.PARTS_PER_CLASS_LABEL}
                  name="partsPerClass"
                  rules={[
                    { required: true, message: VALIDATION_MESSAGES.PARTS_PER_CLASS_REQUIRED },
                    { type: 'number', min: 1, max: reposition.availableParts, message: VALIDATION_MESSAGES.PARTS_PER_CLASS_RANGE(reposition.availableParts) }
                  ]}
                >
                  <InputNumber 
                    min={INPUT_NUMBER_CONFIG.MIN} 
                    max={reposition.availableParts} 
                    placeholder={INPUT_NUMBER_CONFIG.PLACEHOLDER}
                  />
                </Form.Item>
              </>
            )}
            {renderRepositionTimePicker()}
            {values.replacementType === REPLACEMENT_TYPE_VALUES.COMPLETE && (
              <Form.Item
                label={TEXT.SCHEDULE_LABEL}
                name="time"
                rules={[
                  {
                    required: true,
                    message: VALIDATION_MESSAGES.TIME_REQUIRED
                  }
                ]}
              >
                <TimePicker 
                  popupClassName={TIME_PICKER_CONFIG.POPUP_CLASS_NAME} 
                  minuteStep={TIME_PICKER_CONFIG.MINUTE_STEP} 
                  use12Hours={TIME_PICKER_CONFIG.USE_12_HOURS}
                  format={TIME_PICKER_CONFIG.FORMAT}
                />
              </Form.Item>
            )}
          </div>
        </FormWrapper>
      </div>
    </div>
  );
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getClasses, createRepositions }, dispatch);
const mapStateToProps = (state) => ({
  classReducer: state.get('classReducer'),
  groupCancellationReducer: state.get('groupCancellationReducer'),
});

export default connect(mapStateToProps, mapDispatchToProps)(RepositionModal);
